import { useCallback, useEffect, useRef, useState } from "react";

import "@/v2.css";

import LeadModal from "@/components/marketing/LeadModal";

// v2 components (replacing V1 section-by-section per approval gate)
import ContactV2 from "@/components/v2/Contact";
import FaqV2 from "@/components/v2/Faq";
import FinalCtaV2 from "@/components/v2/FinalCta";
import FooterV2 from "@/components/v2/Footer";
import HeroV2 from "@/components/v2/Hero";
import HowItWorksV2 from "@/components/v2/HowItWorks";
import LayersV2 from "@/components/v2/Layers";
import NavV2 from "@/components/v2/Nav";
import PricingV2 from "@/components/v2/Pricing";
import ProblemV2 from "@/components/v2/Problem";
import ProductV2 from "@/components/v2/Product";
import SocialProofV2 from "@/components/v2/SocialProof";
import StatsV2 from "@/components/v2/Stats";
import TechnologiesV2 from "@/components/v2/Technologies";

export default function Marketing() {
    const [modalState, setModalState] = useState({
        open: false,
        inquiryType: "demo_request",
    });
    const fadeRootRef = useRef(null);

    const openDemoModal = useCallback((inquiryType = "demo_request") => {
        setModalState({ open: true, inquiryType });
    }, []);
    const closeDemoModal = useCallback(() => {
        setModalState((s) => ({ ...s, open: false }));
    }, []);

    // IntersectionObserver fade-in for elements with .reveal
    useEffect(() => {
        const root = fadeRootRef.current;
        if (!root || typeof IntersectionObserver === "undefined") return;
        const reduceMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)",
        ).matches;
        const els = root.querySelectorAll(".reveal");
        if (reduceMotion) {
            els.forEach((el) => el.classList.add("is-visible"));
            return;
        }
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("is-visible");
                        io.unobserve(entry.target);
                    }
                });
            },
            { rootMargin: "0px 0px -10% 0px", threshold: 0.08 },
        );
        els.forEach((el) => io.observe(el));

        const safetyTimer = window.setTimeout(() => {
            root.querySelectorAll(".reveal:not(.is-visible)").forEach((el) => {
                el.classList.add("is-visible");
                io.unobserve(el);
            });
        }, 400);

        return () => {
            window.clearTimeout(safetyTimer);
            io.disconnect();
        };
    }, []);

    // Smooth scroll for hash links
    useEffect(() => {
        const handler = (e) => {
            const a = e.target.closest("a[href^='#']");
            if (!a) return;
            const id = a.getAttribute("href").slice(1);
            if (!id) return;
            const target = document.getElementById(id);
            if (!target) return;
            e.preventDefault();
            target.scrollIntoView({ behavior: "smooth", block: "start" });
        };
        document.addEventListener("click", handler);
        return () => document.removeEventListener("click", handler);
    }, []);

    return (
        <div className="cv2 core-site" ref={fadeRootRef}>
            <NavV2 onOpenDemo={() => openDemoModal("demo_call")} />

            <main>
                <HeroV2 onOpenDemo={() => openDemoModal("demo_request")} />
                <StatsV2 />
                <ProblemV2 />
                <LayersV2 />
                <ProductV2 />
                <HowItWorksV2 />
                <SocialProofV2 />
                <PricingV2 />
                <TechnologiesV2 />
                <FaqV2 />
                <ContactV2 onBookDemo={() => openDemoModal("demo_call")} />
                <FinalCtaV2 onOpenDemo={() => openDemoModal("demo_request")} />
            </main>

            <FooterV2 />

            <LeadModal
                open={modalState.open}
                inquiryType={modalState.inquiryType}
                onClose={closeDemoModal}
            />
        </div>
    );
}
